import React, { Component } from 'react'
import {
    Button,
    Box,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
} from '@material-ui/core';
import { Add, Close, Delete, Edit } from '@material-ui/icons';
import Service from "../../config/networkutils";
import Auth from '../../utils/authenticate';
import CheckIcon from '@material-ui/icons/Check';
import moment from 'moment';
import { withSnackbar } from 'notistack';


const token = Auth.token();

class BankHolidayTable extends Component {

    constructor(props) {
        super(props)
        this.state = {
            data: [],
            tableLoading: true,
            searchText: '',
            filters: {},
            orderBy: '',
            order: 'asc',
            columns: [{ title: 'Name', field: 'name' }, { title: 'Date', field: 'date' }],
            isAdding: false,
            newRowData: { name: '', date: '' },
            editingRowId: null,
            editingRowData: { name: '', date: '' },
        }
    }

    componentDidMount() {
        this.getData()
    }

    getData = () => {
        Service.get('/api/bank/holidays/get_business_unit_holiday/', {
            headers: {
                Authorization: "Token " + token,
            },
        })
            .then(res => {
                this.setState({ data: res.data, tableLoading: false })

            }).catch(error => {
                console.log(error)
                this.setState({ tableLoading: false })
                if (error.status === 403) {
                    this.props.enqueueSnackbar(error.data.message, {
                        variant: 'warning'
                    })
                } else {
                    this.props.enqueueSnackbar('Something went wrong!', {
                        variant: 'error'
                    });
                }
            })
    }

    notify = (message, variant) => {
        if (this.props.enqueueSnackbar) {
            this.props.enqueueSnackbar(message, { variant });
        }
    }

    toHeaderLabel = (key) => {
        return String(key)
            .replace(/_/g, ' ')
            .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
    }

    getColumns = () => {
        const firstRow = this.state.data && this.state.data.length > 0 ? this.state.data[0] : null;
        if (!firstRow) {
            return [];
        }
        return Object.keys(firstRow).map((key) => ({
            field: key,
            title: this.toHeaderLabel(key),
        }));
    }

    stringifyCell = (value) => {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch (error) {
                return String(value);
            }
        }
        return String(value);
    }

    formatDateDisplay = (value) => {
        const raw = this.stringifyCell(value);
        if (!raw) {
            return '';
        }

        if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
            return raw;
        }

        if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
            const datePart = raw.slice(0, 10);
            const [year, month, day] = datePart.split('-');
            return `${day}-${month}-${year}`;
        }

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
            return raw.replace(/\//g, '-');
        }

        const parsed = new Date(raw);
        if (!Number.isNaN(parsed.getTime())) {
            const day = String(parsed.getDate()).padStart(2, '0');
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const year = parsed.getFullYear();
            return `${day}-${month}-${year}`;
        }

        return raw;
    }

    normalizeDateToYMD = (value) => {
        const raw = this.stringifyCell(value).trim();
        if (!raw) {
            return '';
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            return raw;
        }

        if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
            const [day, month, year] = raw.split('-');
            return `${year}-${month}-${day}`;
        }

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
            const [day, month, year] = raw.split('/');
            return `${year}-${month}-${day}`;
        }

        return raw;
    }

    convertYMDToDMY = (value) => {
        const raw = this.normalizeDateToYMD(value);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            return this.stringifyCell(value);
        }
        const [year, month, day] = raw.split('-');
        return `${day}-${month}-${year}`;
    }

    getApiDateValue = (value) => {
        const ymd = this.normalizeDateToYMD(value);
        if (!ymd) {
            return '';
        }

        const firstDate = this.state.data?.[0]?.date;
        if (typeof firstDate === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(firstDate)) {
            return this.convertYMDToDMY(ymd);
        }

        return ymd;
    }

    getApiErrorMessage = (error) => {
        if (!error) {
            return 'Something went wrong!';
        }
        const data = error.data;
        if (typeof data === 'string' && data.trim()) {
            return data;
        }
        if (data && typeof data.message === 'string' && data.message.trim()) {
            return data.message;
        }
        if (data && typeof data.detail === 'string' && data.detail.trim()) {
            return data.detail;
        }
        return 'Something went wrong!';
    }

    handleSearchChange = (event) => {
        this.setState({
            searchText: event.target.value,
        });
    }

    handleFilterChange = (field, value) => {
        this.setState((prevState) => ({
            filters: {
                ...prevState.filters,
                [field]: value,
            },
        }));
    }

    handleSort = (field) => {
        this.setState((prevState) => {
            const isAsc = prevState.orderBy === field && prevState.order === 'asc';
            return {
                orderBy: field,
                order: isAsc ? 'desc' : 'asc',
            };
        });
    }

    getFilteredData = (rows, columns) => {
        const { searchText, filters } = this.state;
        const search = searchText.trim().toLowerCase();

        return rows.filter((row) => {
            const matchesSearch = !search || columns.some((column) => {
                return this.stringifyCell(row[column.field]).toLowerCase().includes(search);
            });

            const matchesFilters = columns.every((column) => {
                const filterValue = (filters[column.field] || '').trim().toLowerCase();
                if (!filterValue) {
                    return true;
                }
                return this.stringifyCell(row[column.field]).toLowerCase().includes(filterValue);
            });

            return matchesSearch && matchesFilters;
        });
    }

    getSortedData = (rows) => {
        const { orderBy, order } = this.state;
        if (!orderBy) {
            return rows;
        }

        const sorted = [...rows].sort((a, b) => {
            const aValue = this.stringifyCell(a[orderBy]).toLowerCase();
            const bValue = this.stringifyCell(b[orderBy]).toLowerCase();
            if (aValue < bValue) {
                return -1;
            }
            if (aValue > bValue) {
                return 1;
            }
            return 0;
        });

        return order === 'asc' ? sorted : sorted.reverse();
    }

    getRowId = (row, index) => {
        if (row && row.id !== undefined && row.id !== null) {
            return String(row.id);
        }
        if (row && row.pk !== undefined && row.pk !== null) {
            return String(row.pk);
        }
        return `row-${index}`;
    }

    getPayload = (source) => {
        const payload = {};
        this.state.columns.forEach((column) => {
            payload[column.field] = source[column.field] !== undefined ? source[column.field] : '';
        });
        return payload;
    }

    isRowValid = (rowData) => {
        return this.state.columns.every((column) => {
            const value = rowData[column.field];
            return String(value !== undefined && value !== null ? value : '').trim().length > 0;
        });
    }

    getMissingFields = (rowData) => {
        return this.state.columns
            .filter((column) => {
                const value = rowData[column.field];
                return String(value !== undefined && value !== null ? value : '').trim().length === 0;
            })
            .map((column) => column.title);
    }

    validateRowBeforeSubmit = (rowData, mode) => {
        const missingFields = this.getMissingFields(rowData);
        if (missingFields.length > 0) {
            this.notify(`Please fill required field(s): ${missingFields.join(', ')}`, 'warning');
            return false;
        }

        if (rowData.date && !/^\d{4}-\d{2}-\d{2}$/.test(this.normalizeDateToYMD(rowData.date))) {
            this.notify('Please enter a valid date.', 'warning');
            return false;
        }

        if (mode === 'add') {
            const duplicate = this.state.data.some((item) => {
                const sameName = this.stringifyCell(item.name).trim().toLowerCase() === this.stringifyCell(rowData.name).trim().toLowerCase();
                const sameDate = this.normalizeDateToYMD(item.date) === this.normalizeDateToYMD(rowData.date);
                return sameName && sameDate;
            });
            if (duplicate) {
                this.notify('This bank holiday already exists.', 'warning');
                return false;
            }
        }

        return true;
    }

    handleStartAdd = () => {
        const newRowData = this.state.columns.reduce((acc, column) => {
            acc[column.field] = '';
            return acc;
        }, {});
        this.setState({ isAdding: true, newRowData, editingRowId: null });
    }

    handleCancelAdd = () => {
        this.setState({ isAdding: false, newRowData: { name: '', date: '' } });
    }

    handleAddFieldChange = (field, value) => {
        this.setState((prevState) => ({
            newRowData: {
                ...prevState.newRowData,
                [field]: value,
            },
        }));
    }

    handleAddRow = () => {
        const payload = {
            ...this.getPayload(this.state.newRowData),
            date: this.getApiDateValue(this.state.newRowData.date),
            year: moment(this.getApiDateValue(this.state.newRowData.date), 'YYYY-MM-DD').year(),
        };
        if (!this.validateRowBeforeSubmit(payload, 'add')) {
            return;
        }

        this.setState({ tableLoading: true });
        Service.post('/api/bank/holidays/', {
            headers: {
                Authorization: "Token " + token,
            },
            data: payload,
        }).then(() => {
            this.notify('Added successfully.', 'success');
            this.setState({ isAdding: false, newRowData: { name: '', date: '' } }, this.getData);
        }).catch((error) => {
            console.log(error);
            this.setState({ tableLoading: false });
            this.notify(this.getApiErrorMessage(error), 'error');
        });
    }

    handleStartEdit = (row, index) => {
        const rowId = this.getRowId(row, index);
        this.setState({
            editingRowId: rowId,
            editingRowData: this.getPayload(row),
            isAdding: false,
        });
    }

    handleCancelEdit = () => {
        this.setState({ editingRowId: null, editingRowData: { name: '', date: '' } });
    }

    handleEditFieldChange = (field, value) => {
        this.setState((prevState) => ({
            editingRowData: {
                ...prevState.editingRowData,
                [field]: value,
            },
        }));
    }

    handleSaveEdit = (row) => {
        const rowId = row?.id || row?.pk;
        if (!rowId) {
            this.props.enqueueSnackbar('Unable to identify row for update.', { variant: 'warning' });
            return;
        }

        const payload = {
            ...this.getPayload(this.state.editingRowData),
            date: this.getApiDateValue(this.state.editingRowData.date),
        };
        if (!this.validateRowBeforeSubmit(payload, 'edit')) {
            return;
        }

        this.setState({ tableLoading: true });
        Service.put(`/api/bank/holidays/${rowId}/`, {
            headers: {
                Authorization: "Token " + token,
            },
            data: {
                ...row,
                ...payload,
            },
        }).then(() => {
            this.props.enqueueSnackbar('Updated successfully.', { variant: 'success' });
            this.setState({ editingRowId: null, editingRowData: { name: '', date: '' } }, this.getData);
        }).catch((error) => {
            console.log(error);
            this.setState({ tableLoading: false });
            this.props.enqueueSnackbar(this.getApiErrorMessage(error), { variant: 'error' });
        });
    }

    handleDeleteRow = (row) => {
        const rowId = row?.id || row?.pk;
        if (!rowId) {
            this.props.enqueueSnackbar('Unable to identify row for delete.', { variant: 'warning' });
            return;
        }



        this.setState({ tableLoading: true });
        Service.delete(`/api/bank/holidays/${rowId}/`, {
            headers: {
                Authorization: "Token " + token,
            },
        }).then(() => {
            this.props.enqueueSnackbar('Deleted successfully.', { variant: 'success' });
            this.getData();
        }).catch((error) => {
            console.log(error);
            this.setState({ tableLoading: false });
            this.props.enqueueSnackbar('Something went wrong!', { variant: 'error' })
        });
    }

    render() {
        const filteredData = this.getFilteredData(this.state.data, this.state.columns);
        const sortedData = this.getSortedData(filteredData);
        const { filters, orderBy, order, searchText } = this.state;

        return (
            <div>
                <Paper elevation={1} style={{ marginTop: 16 }}>
                    <TableContainer>
                        <Box p={2} display='flex' justifyContent='flex-end'>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<Add />}
                                onClick={this.handleStartAdd}
                                style={{ marginRight: 8 }}
                            >
                                Add
                            </Button>
                            <TextField
                                label='Search'
                                variant='outlined'
                                size='small'
                                value={searchText}
                                onChange={this.handleSearchChange}
                            />
                        </Box>

                        <Table size='small'>
                            <TableHead>
                                <TableRow>
                                    {this.state.columns.map((column) => (
                                        <TableCell key={column.field}>
                                            <TableSortLabel
                                                active={orderBy === column.field}
                                                direction={orderBy === column.field ? order : 'asc'}
                                                onClick={() => this.handleSort(column.field)}
                                            >
                                                {column.title}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                                <TableRow>
                                    {this.state.columns.map((column) => (
                                        <TableCell key={`${column.field}-filter`}>
                                            <TextField
                                                type={column.field === 'date' ? 'date' : 'text'}
                                                variant='outlined'
                                                size='small'
                                                fullWidth
                                                InputLabelProps={column.field === 'date' ? { shrink: true } : undefined}
                                                value={filters[column.field] || ''}
                                                onChange={(event) => this.handleFilterChange(column.field, event.target.value)}
                                                placeholder={`Filter ${column.title}`}
                                            />
                                        </TableCell>
                                    ))}
                                    <TableCell />
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {this.state.isAdding && (
                                    <TableRow>
                                        {this.state.columns.map((column) => (
                                            <TableCell key={`add-${column.field}`}>
                                                <TextField
                                                    type={column.field === 'date' ? 'date' : 'text'}
                                                    variant='outlined'
                                                    size='small'
                                                    fullWidth
                                                    InputLabelProps={column.field === 'date' ? { shrink: true } : undefined}
                                                    value={this.state.newRowData[column.field] || ''}
                                                    onChange={(event) => this.handleAddFieldChange(column.field, event.target.value)}
                                                />
                                            </TableCell>
                                        ))}
                                        <TableCell>
                                            <IconButton size='small' color='primary' onClick={this.handleAddRow}>
                                                <CheckIcon />
                                            </IconButton>
                                            <IconButton size='small' onClick={this.handleCancelAdd}>
                                                <Close />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {this.state.tableLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={this.state.columns.length + 1} align='center'>
                                            <CircularProgress size={24} />
                                        </TableCell>
                                    </TableRow>
                                ) : sortedData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={this.state.columns.length + 1} align='center'>
                                            No records found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedData.map((row, index) => {
                                        const rowId = this.getRowId(row, index);
                                        const isEditing = rowId === this.state.editingRowId;

                                        return (
                                            <TableRow hover key={`${index}`}>
                                                {isEditing ? (
                                                    <>
                                                        {this.state.columns.map((column) => (
                                                            <TableCell key={`${index}-${column.field}`}>
                                                                <TextField
                                                                    type={column.field === 'date' ? 'date' : 'text'}
                                                                    variant='outlined'
                                                                    size='small'
                                                                    fullWidth
                                                                    InputLabelProps={column.field === 'date' ? { shrink: true } : undefined}
                                                                    value={this.state.editingRowData[column.field] || ''}
                                                                    onChange={(event) => this.handleEditFieldChange(column.field, event.target.value)}
                                                                />
                                                            </TableCell>
                                                        ))}
                                                        <TableCell>
                                                            <IconButton size='small' color='primary' onClick={() => this.handleSaveEdit(row)}>
                                                                <CheckIcon />
                                                            </IconButton>
                                                            <IconButton size='small' onClick={this.handleCancelEdit}>
                                                                <Close />
                                                            </IconButton>
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        {this.state.columns.map((column) => (
                                                            <TableCell key={`${index}-${column.field}`}>
                                                                {column.field === 'date'
                                                                    ? (this.formatDateDisplay(row[column.field]) || '-')
                                                                    : (this.stringifyCell(row[column.field]) || '-')}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell>
                                                            <IconButton
                                                                size='small'
                                                                color='primary'
                                                                onClick={() => this.handleStartEdit(row, index)}
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                            <IconButton
                                                                size='small'
                                                                color='secondary'
                                                                onClick={() => this.handleDeleteRow(row)}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </div>
        )
    }
}

export default withSnackbar(BankHolidayTable);
